import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Satellite as SatelliteIcon,
  Notifications as NotificationsIcon,
  ChevronLeft as ChevronLeftIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  Router as RouterIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { alarmsService } from '../services/api';

const drawerWidth = 220;
const drawerWidthCollapsed = 60;

function Layout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [alarmCount, setAlarmCount] = useState(0);

  // Carica conteggio allarmi attivi
  useEffect(() => {
    const loadAlarmCount = async () => {
      try {
        const data = await alarmsService.getActive();
        setAlarmCount(data.alarms?.length || 0);
      } catch (error) {
        console.error('Errore caricamento allarmi:', error);
      }
    };
    loadAlarmCount();
    // Aggiorna ogni 30 secondi
    const interval = setInterval(loadAlarmCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Menu items dinamico in base al ruolo
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Allarmi', icon: <WarningIcon />, path: '/alarms' },
    ...(isAdmin ? [{ text: 'Sensori', icon: <RouterIcon />, path: '/sensors' }] : []),
    ...(isAdmin ? [{ text: 'Impostazioni', icon: <SettingsIcon />, path: '/settings' }] : []),
    ...(isAdmin ? [{ text: 'Utenti', icon: <PeopleIcon />, path: '/users' }] : []),
  ];

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    if (onLogout) onLogout();
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const currentWidth = drawerOpen ? drawerWidth : drawerWidthCollapsed;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          px: (isMobile || drawerOpen) ? 2 : 1,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: (isMobile || drawerOpen) ? 'flex-start' : 'center',
          gap: 1.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          minHeight: 56,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <SatelliteIcon sx={{ fontSize: 24 }} />
        {(isMobile || drawerOpen) && (
          <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: 0.5 }}>
            2mon
          </Typography>
        )}
      </Box>

      {/* Menu items */}
      <List sx={{ flex: 1, pt: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={(isMobile || drawerOpen) ? '' : item.text} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  mx: 0.5,
                  borderRadius: 1,
                  minHeight: 44,
                  justifyContent: (isMobile || drawerOpen) ? 'initial' : 'center',
                  px: (isMobile || drawerOpen) ? 2 : 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '& .MuiListItemIcon-root': { color: 'white' },
                    '&:hover': { backgroundColor: 'primary.dark' },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: (isMobile || drawerOpen) ? 2 : 0,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(isMobile || drawerOpen) && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* Toggle button - solo desktop */}
      {!isMobile && (
        <>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
            <IconButton onClick={handleDrawerToggle} size="small">
              <ChevronLeftIcon
                sx={{
                  transform: drawerOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Box>
        </>
      )}

      {/* Footer */}
      {(isMobile || drawerOpen) && (
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, lineHeight: 1.3, display: 'block', textAlign: 'center' }}>
            developer by marcuzz0
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, lineHeight: 1.3, display: 'block', textAlign: 'center' }}>
            ver. 1.1
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Drawer Mobile */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Drawer Desktop */
        <Drawer
          variant="permanent"
          sx={{
            width: currentWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: currentWidth,
              boxSizing: 'border-box',
              transition: 'width 0.2s ease-in-out',
              overflowX: 'hidden',
              borderRight: '1px solid rgba(0,0,0,0.08)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflow: 'hidden',
          bgcolor: 'background.default',
          position: 'relative',
        }}
      >
        {/* Hamburger menu mobile */}
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1000,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Icone in alto a destra */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 12,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Tooltip title="Notifiche">
            <IconButton size="small" onClick={() => navigate('/alarms')}>
              <Badge badgeContent={alarmCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleMenuClick} size="small">
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: 14 }}>
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
        </Box>
        {children}
      </Box>

      {/* User menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2">{user?.username || 'Utente'}</Typography>
        </MenuItem>
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
            {user?.role || ''}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default Layout;
